/* istanbul ignore file */
import React, { Component } from 'react'
import analytics from '@adc/analytics'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import PropTypes from 'prop-types'
import { Placeholder } from 'semantic-ui-react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import Button from '../../elementals/button'
import { adglareHtmlOverride } from './fixture'
import {
  linkPropType,
  AddressInfoLine,
  CardImage,
  PropertyInfo,
  PropertyType,
  PropertyValue,
  ListingCardIndicator,
} from './cardParts'

import cls from './styles.scss'
import { getV2ApplicableBadges } from '../badges'
import { windowSizes } from '../../../constants'
import {
  getCurrentBid,
  getERV,
  shouldDisplayCurrentBid,
  shouldDisplayReserveMet,
} from './cardHelpers'

const cx = classnames.bind(cls)

// Enum definition
export const ListingCardViewModeEnum = {
  HORIZONTAL: 'horizontal',
  GRID: 'grid',
  DEFAULT: 'default',
}

const defaultAdglareConfiguration = {
  htmlOverride: adglareHtmlOverride,
}

class ListingCard extends Component {
  static propTypes = {
    className: PropTypes.string,
    elmId: PropTypes.string,
    listing: PropTypes.shape({
      injectedContentConfiguration: PropTypes.shape({
        main_text: PropTypes.string.isRequired,
        sub_text: PropTypes.string,
        buttons: PropTypes.arrayOf(
          PropTypes.shape({
            text: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            link: PropTypes.string.isRequired,
          })
        ),
      }),
      listing_status: PropTypes.string,
      listing_id: PropTypes.string.isRequired,
      listing_status_group: PropTypes.string.isRequired,
      listing_status_label: PropTypes.string.isRequired,
      primary_photo: PropTypes.string,
      listing_photos_count: PropTypes.number,
      listing_page_path: PropTypes.string,
      reserve_price: PropTypes.number,
      is_hot: PropTypes.bool,
      formatted_address: PropTypes.arrayOf(PropTypes.string),
      listing_summary: PropTypes.shape({
        is_remote_bid_enabled: PropTypes.bool,
        is_remote_before_and_during_auction_enabled: PropTypes.bool,
        show_opening_bid: PropTypes.bool,
      }),
      listing_configuration: PropTypes.shape({
        product_type: PropTypes.string,
        broker_commission: PropTypes.number,
        financing_available: PropTypes.bool,
        buyer_premium_available: PropTypes.bool,
        interior_access_allowed: PropTypes.bool,
        occupancy_status: PropTypes.string,
        is_reserve_displayed: PropTypes.bool,
        asset_type: PropTypes.string,
        is_direct_offer_enabled: PropTypes.bool,
        is_third_party_online: PropTypes.bool,
      }),
      program_configuration: PropTypes.shape({
        program_enrollment_code: PropTypes.string,
      }),
      event: PropTypes.shape({
        event_code: PropTypes.string,
      }),
      seller_property: PropTypes.shape({
        street_description: PropTypes.string,
        municipality: PropTypes.string,
        country_primary_subdivision: PropTypes.string,
        country_secondary_subdivision: PropTypes.string,
        postal_code: PropTypes.string,
      }),
      primary_property: PropTypes.shape({
        property_id: PropTypes.string,
        summary: PropTypes.shape({
          street_address: PropTypes.string,
          total_bedrooms: PropTypes.number,
          total_bathrooms: PropTypes.number,
          square_footage: PropTypes.number,
          lot_size: PropTypes.number,
          year_built: PropTypes.number,
          valuation: PropTypes.number,
        }),
        is_currently_saved: PropTypes.bool,
        is_newly_listed: PropTypes.bool,
        current_user_tracking_state: PropTypes.shape({
          is_seen: PropTypes.bool,
          is_updated: PropTypes.bool,
        }),
      }),
      auction: PropTypes.shape({
        start_date: PropTypes.string,
        end_date: PropTypes.string,
        starting_bid: PropTypes.number,
        is_online: PropTypes.bool,
      }),
      external_information: PropTypes.shape({
        collateral: PropTypes.shape({
          summary: PropTypes.arrayOf(
            PropTypes.shape({
              estimate: PropTypes.number,
              high: PropTypes.number,
              type: PropTypes.string,
            })
          ),
        }),
      }),
    }).isRequired,
    showSave: PropTypes.bool,
    showAdContent: PropTypes.bool,
    onSaveToggle: PropTypes.func,
    linkProps: linkPropType,
    viewMode: PropTypes.oneOf(Object.values(ListingCardViewModeEnum)),
    loading: PropTypes.bool,
    featureFlags: PropTypes.object,
    isFocused: PropTypes.bool,
    verifiedImage: PropTypes.string,
    updateVerifiedImage: PropTypes.func,
    viewingFrom: PropTypes.oneOf(
      Object.values(graph.enums.CurrentTargetAudience)
    ),
  }

  static defaultProps = {
    className: '',
    elmId: 'listing_card',
    listing: {
      listing_id: '0',
      listing_page_path: '',
      is_hot: false,
      primary_photo: '',
      listing_photos_count: 0,
      auction: {},
      event: {},
      primary_property: {
        summary: {},
        physical_structures: [{}],
        current_user_tracking_state: {},
      },
      listing_configuration: { product_type: '' },
      external_information: {},
    },
    showSave: true,
    onSaveToggle: _.noop,
    linkProps: {
      onClick: _.noop,
      onMouseLeave: _.noop,
      href: null,
    },
    loading: false,
    viewMode: ListingCardViewModeEnum.DEFAULT,
    showAdContent: false,
    featureFlags: {},
    isFocused: false,
    viewingFrom: graph.enums.CurrentTargetAudience.Buyer,
  }

  onMouseLeave = () => {
    const { linkProps } = this.props
    linkProps.onMouseLeave && linkProps.onMouseLeave()
  }

  stopPropagationClick = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  getV2AnalyticsAttributes = listing => {
    return {
      badges: this.getVisibleBadgesLabels(),
      isReserveMetShown: shouldDisplayReserveMet(listing),
      displayedCurrentBid: shouldDisplayCurrentBid(listing)
        ? getCurrentBid(listing)
        : null,
      displayedERV: getERV(listing),
    }
  }

  getVisibleBadgesLabels = () =>
    getV2ApplicableBadges(this.props.listing, this.getMaxBadges())
      .map(badge => badge.titleV2)
      .value()

  getMaxBadges = () => {
    let maxBadges = 3
    if (this.props.viewMode === ListingCardViewModeEnum.HORIZONTAL) {
      maxBadges = window.innerWidth < windowSizes.MEDIUM_WIDTH ? 1 : 2
    }
    return maxBadges
  }

  onMouseClick = () => {
    const { linkProps, listing } = this.props
    const onClickParams = this.getV2AnalyticsAttributes(listing)
    linkProps.onClick && linkProps.onClick(onClickParams)
  }

  debounceOnSave = _.debounce(
    async property =>
      await this.props.onSaveToggle(property, this.constructTrackingContext()),
    500,
    { leading: true, trailing: false }
  )

  onSaveToggle = property => {
    this.debounceOnSave(property)
  }

  constructTrackingContext = () => {
    const { listing } = this.props
    let trackingInfo = analytics.standardTracking.listing(listing)

    trackingInfo = {
      ...trackingInfo,
      ...this.getV2AnalyticsAttributes(listing),
    }

    return trackingInfo
  }

  renderPlaceholder = () => (
    <div className={cx('property-value-container')}>
      <Placeholder style={{ margin: 12, width: '100%' }}>
        <Placeholder.Header>
          <Placeholder.Line length='short' />
        </Placeholder.Header>
        <Placeholder.Paragraph style={{ width: '100%' }}>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    </div>
  )

  render() {
    const {
      className,
      elmId,
      listing,
      showSave,
      linkProps,
      viewMode,
      showAdContent,
      loading,
      featureFlags,
      isFocused,
      verifiedImage,
      updateVerifiedImage,
      viewingFrom,
    } = this.props

    const isCancelled =
      listing.listing_status === graph.enums.ListingStatusGroup.Canceled

    const isHorizontalView = viewMode === ListingCardViewModeEnum.HORIZONTAL

    const { injectedContentConfiguration = defaultAdglareConfiguration } =
      listing

    const renderInjectedCardContent = () => {
      if (injectedContentConfiguration.htmlOverride) {
        return (
          <div
            id='injectable-html-override-target'
            data-elm-id='injectable-html-override-target'
            className={cx('html-override-container')}
            dangerouslySetInnerHTML={{
              __html: injectedContentConfiguration.htmlOverride,
            }}
          />
        )
      } else {
        return (
          <>
            {' '}
            <div className={cx('property-card-image-container')}>
              <span
                className={cx('property-card-image-background')}
                style={{
                  backgroundImage: `url(${injectedContentConfiguration.main_photo})`,
                }}
                role='img'
                aria-label='current-campaign-image'
              />
            </div>
            <div className={cx('injected-content-body')}>
              <div className={cx('header-text')}>
                {injectedContentConfiguration.main_text}
              </div>
              {injectedContentConfiguration.sub_text && (
                <div className={cx('sub-header-text')}>
                  {injectedContentConfiguration.sub_text}
                </div>
              )}
              {injectedContentConfiguration.buttons.length > 0 && (
                <div>
                  {injectedContentConfiguration.buttons.map((button, index) => (
                    <a
                      href={button.link}
                      className={cx(button.className || '')}
                      role={button.role}
                      id={button.elmId}
                    >
                      <Button
                        key={index}
                        size='small'
                        role={button.role}
                        elmId={`${button.elmId}_button`}
                        aria-label={button['aria-label']}
                      >
                        {button.text}
                      </Button>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </>
        )
      }
    }

    return (
      <div
        className={cx(
          'root',
          { 'map-card': isHorizontalView },
          { 'grid-card': viewMode === ListingCardViewModeEnum.GRID },
          { 'root-card-disabled': isCancelled },
          className
        )}
        onMouseLeave={this.onMouseLeave}
      >
        <a
          href={isCancelled || showAdContent ? null : linkProps.href}
          className={cx('card-link')}
          onClick={this.onMouseClick}
        >
          <div
            className={cx(
              'base-card',
              {
                seen: listing.primary_property?.current_user_tracking_state
                  ?.is_seen,
              },
              {
                focused: isFocused,
              },
              className
            )}
            data-elm-id={`property_card_${elmId}`}
          >
            {showAdContent ? (
              renderInjectedCardContent()
            ) : (
              <>
                <CardImage
                  listing={listing}
                  elmId={elmId}
                  showSave={showSave && !showAdContent}
                  onSaveToggle={isCancelled ? () => {} : this.onSaveToggle}
                  stopPropagationClick={this.stopPropagationClick}
                  isHorizontalView={isHorizontalView}
                  loading={loading}
                  maxBadges={this.getMaxBadges()}
                  isCancelled={isCancelled}
                  featureFlags={featureFlags}
                  verifiedImage={verifiedImage}
                  updateVerifiedImage={updateVerifiedImage}
                  viewingFrom={viewingFrom}
                />
                {loading ? (
                  this.renderPlaceholder()
                ) : (
                  <div className={cx('property-card-body')}>
                    <PropertyValue
                      listing={listing}
                      elmId={elmId}
                      loading={loading}
                      featureFlags={featureFlags}
                    />
                    <AddressInfoLine
                      address={listing.formatted_address}
                      elmId={elmId}
                      isCancelled={isCancelled}
                      isHorizontalView={isHorizontalView}
                    />
                    <PropertyInfo
                      listing={listing}
                      elmId={elmId}
                      isCancelled={isCancelled}
                    />
                    <ListingCardIndicator
                      listing={listing}
                      elmId={elmId}
                      className={cx('listing-card-asset-info')}
                    />
                    <PropertyType
                      listing={listing}
                      elmId={elmId}
                      featureFlags={featureFlags}
                      isHorizontalView={isHorizontalView}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </a>
      </div>
    )
  }
}

// does not include fields required for badges which will have to be included separately
ListingCard[GRAPH_API_FRAGMENT_DEFINITION] =
  blueprint.listing.fragments.listingCard.definition

ListingCard[GRAPH_API_FRAGMENT_REFERENCE] =
  blueprint.listing.fragments.listingCard.reference

export default ListingCard
